import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import { useReadOnly, ReadOnlyBanner } from "../hooks/useReadOnly";
import api from "../utils/api";
import { Bookmark, Pencil, Trash2, Check, X, Search, Download, Upload } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function FileTemplatesPage() {
  const readOnly = useReadOnly();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSheet, setEditSheet] = useState("");
  const [editSkip, setEditSkip] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const importRef = useRef();

  const downloadJSON = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      alert("Export failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/file-templates/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${res.data.imported} template(s)`);
      load();
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      importRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/file-templates");
      setTemplates(r.data);
    } catch (e) {
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditSheet(t.sheet_name || "");
    setEditSkip(t.skip_rows || 0);
    setError("");
  };

  const cancelEdit = () => { setEditingId(null); setError(""); };

  const saveEdit = async (id) => {
    if (!editName.trim()) { setError("Name is required"); return; }
    setSaving(true);
    setError("");
    try {
      const r = await api.put(`/file-templates/${id}`, {
        name: editName.trim(),
        sheet_name: editSheet || null,
        skip_rows: Number(editSkip || 0),
      });
      setTemplates(prev => prev.map(t => t.id === id ? r.data : t));
      setEditingId(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setDeletingId(t.id);
    try {
      await api.delete(`/file-templates/${t.id}`);
      setTemplates(prev => prev.filter(x => x.id !== t.id));
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const visible = templates.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.sheet_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (t.created_by_email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "1180px", margin: "0 auto" }}>
        <ReadOnlyBanner what="file templates" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.5rem", color: "var(--text-primary)", margin: 0 }}>
              File Templates
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>
              Saved sheet + skip-rows configurations. Apply from the file upload slot in any reconciliation wizard.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "var(--bg-subtle)", borderRadius: "0.5rem", padding: "0.375rem 0.75rem", border: "1px solid var(--border-default)" }}>
              <Search size={13} style={{ color: "var(--text-muted)" }} />
              <input
                data-testid="tpl-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search templates…"
                style={{ border: "none", background: "none", outline: "none", fontSize: "0.82rem", color: "var(--text-primary)", width: "220px" }}
              />
            </div>
            <input ref={importRef} type="file" accept=".json" style={{ display: "none" }} onChange={e => handleImport(e.target.files?.[0])} />
            <button data-testid="tpl-import-btn" className="btn-secondary" onClick={() => importRef.current?.click()}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem" }}>
              <Upload size={13} /> Import
            </button>
            <button data-testid="tpl-export-all-btn" className="btn-secondary"
              onClick={() => downloadJSON("/file-templates/export-all", "file_templates_export.json")}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem" }}>
              <Download size={13} /> Export All
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: "0.5rem 0.875rem", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", marginBottom: "0.75rem", fontSize: "0.82rem", color: "#991B1B" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div className="recon-card" style={{ padding: "3rem", textAlign: "center" }}>
            <Bookmark size={36} style={{ color: "var(--text-muted)", marginBottom: "1rem" }} />
            <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 0.5rem" }}>
              {search ? "No templates match your search" : "No file templates saved yet"}
            </p>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>
              Templates are created from the upload slot when you click "Save as template" in the reconciliation wizard.
            </p>
          </div>
        ) : (
          <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: "820px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-default)", background: "var(--bg-subtle)" }}>
                  {["Name", "Sheet", "Skip rows", "Created by", "Date", ""].map((h, i) => (
                    <th key={i} style={{ padding: "0.625rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => (
                  <tr key={t.id} data-testid={`tpl-row-${t.id}`} style={{ borderBottom: "1px solid var(--border-default)" }}>
                    {editingId === t.id ? (
                      <>
                        <td style={{ padding: "0.5rem 0.75rem" }}>
                          <input data-testid="tpl-edit-name"
                            autoFocus className="recon-input" value={editName} onChange={e => setEditName(e.target.value)}
                            style={{ width: "100%", boxSizing: "border-box", fontSize: "0.82rem", minWidth: "220px" }} />
                        </td>
                        <td style={{ padding: "0.5rem 0.5rem" }}>
                          <input data-testid="tpl-edit-sheet"
                            className="recon-input" value={editSheet} onChange={e => setEditSheet(e.target.value)}
                            placeholder="Sheet1" style={{ width: "160px", boxSizing: "border-box", fontSize: "0.82rem" }} />
                        </td>
                        <td style={{ padding: "0.5rem 0.5rem" }}>
                          <input data-testid="tpl-edit-skip"
                            type="number" min="0" max="200"
                            className="recon-input" value={editSkip} onChange={e => setEditSkip(Number(e.target.value))}
                            style={{ width: "80px", boxSizing: "border-box", fontSize: "0.82rem" }} />
                        </td>
                        <td style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                          {t.created_by_email || "—"}
                        </td>
                        <td style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                          {fmt(t.created_at)}
                        </td>
                        <td style={{ padding: "0.5rem 0.75rem" }}>
                          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "nowrap" }}>
                            <button data-testid="tpl-save-edit"
                              onClick={() => saveEdit(t.id)}
                              disabled={saving}
                              style={{ background: "var(--sage)", color: "white", border: "none", borderRadius: "0.375rem", padding: "0.3rem 0.6rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.75rem" }}>
                              <Check size={12} /> {saving ? "…" : "Save"}
                            </button>
                            <button data-testid="tpl-cancel-edit"
                              onClick={cancelEdit}
                              style={{ background: "none", border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.3rem 0.5rem", cursor: "pointer", color: "var(--text-muted)" }}>
                              <X size={13} />
                            </button>
                          </div>
                          {error && editingId === t.id && (
                            <p style={{ fontSize: "0.68rem", color: "#DC2626", margin: "0.25rem 0 0" }}>{error}</p>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: "0.625rem 1rem", fontWeight: 500, color: "var(--text-primary)" }}>{t.name}</td>
                        <td style={{ padding: "0.625rem 0.75rem", color: "var(--text-secondary)", fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem" }}>
                          {t.sheet_name || <span style={{ color: "var(--text-muted)" }}>—</span>}
                        </td>
                        <td style={{ padding: "0.625rem 0.75rem", color: "var(--text-secondary)" }}>
                          <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem" }}>{t.skip_rows || 0}</span>
                        </td>
                        <td style={{ padding: "0.625rem 0.75rem", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                          {t.created_by_email || "—"}
                        </td>
                        <td style={{ padding: "0.625rem 0.75rem", color: "var(--text-muted)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                          {fmt(t.created_at)}
                        </td>
                        <td style={{ padding: "0.625rem 0.75rem", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", gap: "0.35rem" }}>
                            <button data-testid={`tpl-export-${t.id}`}
                              onClick={() => downloadJSON(`/file-templates/${t.id}/export`, `template_${t.name.replace(/\s+/g, "_")}.json`)}
                              style={{ background: "none", border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.3rem 0.55rem", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.72rem" }}>
                              <Download size={11} /> Export
                            </button>
                            <button data-testid={`tpl-edit-${t.id}`}
                              onClick={() => startEdit(t)}
                              style={{ background: "none", border: "1px solid var(--sage-border)", borderRadius: "0.375rem", padding: "0.3rem 0.55rem", cursor: "pointer", color: "var(--sage)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.72rem" }}>
                              <Pencil size={11} /> Edit
                            </button>
                            <button data-testid={`tpl-delete-${t.id}`}
                              onClick={() => handleDelete(t)}
                              disabled={deletingId === t.id}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: "0.3rem" }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ padding: "0.5rem 1rem", fontSize: "0.72rem", color: "var(--text-muted)", borderTop: "1px solid var(--border-default)", background: "var(--bg-subtle)" }}>
              {visible.length} template{visible.length !== 1 ? "s" : ""}{search && ` matching "${search}"`}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
